/**
 * Core Deposit reconciler — the in-house, app-free replacement for the UpCharge Fees app.
 *
 * Background: refundable "core charges" (deposits on remanufactured parts) were added by the
 * UpCharge app, which probed/added its entire ~2,985-fee catalog on every cart action, got
 * rate-limited by Shopify (HTTP 429), and left a stuck full-screen loading overlay that blocked
 * customers from ordering. This module is the opposite by design:
 *
 *   - The deposit variant for each core part is resolved SERVER-SIDE in Liquid from the part's
 *     `custom.core_deposit_variant` metafield and emitted as hidden `_core_*` line-item
 *     properties on the part line (see blocks/buy-buttons.liquid + snippets/quick-add.liquid).
 *     The browser makes ZERO `/variants` lookups — there is no catalog to probe.
 *   - On every cart change we run ONE idempotent reconcile pass that keeps exactly one deposit
 *     line (qty 1) per distinct core part in the cart, mutating the cart STRICTLY SEQUENTIALLY
 *     (never concurrent) — the same no-concurrency discipline as assets/live-pricing.js — so we
 *     never trip Shopify's cart rate limit.
 *   - Every fetch FAILS OPEN: on 429 we retry once, then give up silently. We never block the
 *     add-to-cart button or checkout and never show an overlay. A miss self-heals on the next
 *     cart event.
 *
 * Deposit quantity is always 1: every core fee in the migrated catalog is `is_onetime` (one
 * deposit per order, not per unit). If a future part needs a per-unit deposit, scale qty here.
 *
 * See core-deposit-build-plan.md for the full design + the matching data setup.
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { debounce } from '@theme/utilities';

/** Deposit variants are the single "Core Deposit" product; SKUs are cents-based: CORE-DEP-<cents>. */
const DEPOSIT_SKU_PREFIX = 'CORE-DEP-';

/** Tag our own re-render dispatch so our listener ignores it (no reconcile loop). */
const SOURCE = 'core-deposit';

// Hidden line-item properties emitted by Liquid on the PART line.
const P_PART_PRODUCT = '_core_for_product';
const P_PART_VARIANT = '_core_deposit_variant';
// Hidden line-item properties this module sets on the DEPOSIT line.
const P_DEPOSIT = '_core_deposit';
const P_DEPOSIT_FOR = '_core_for';

let running = false;
let rerunRequested = false;

/**
 * GET the current cart, or null on failure (fail-open).
 * @returns {Promise<{items: any[], item_count: number}|null>}
 */
async function getCart() {
  try {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    return r.ok ? await r.json() : null;
  } catch (_e) {
    return null;
  }
}

/**
 * One cart mutation with a single 429 retry, then fail-open. Never throws.
 * @param {string} url - '/cart/add.js' | '/cart/change.js'
 * @param {object} body
 * @returns {Promise<boolean>} whether the mutation succeeded
 */
async function mutateOnce(url, body) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      if (r.ok) return true;
      if (r.status === 429 && attempt === 0) {
        await new Promise((res) => setTimeout(res, 800));
        continue; // retry once, then give up
      }
      return false;
    } catch (_e) {
      return false;
    }
  }
  return false;
}

/** A cart line's property value, or undefined. */
function prop(line, key) {
  return line && line.properties ? line.properties[key] : undefined;
}

/** Is this line one of our deposit lines? */
function isDepositLine(line) {
  return Boolean(prop(line, P_DEPOSIT)) || String(line.sku || '').startsWith(DEPOSIT_SKU_PREFIX);
}

/** Is this line the theme-managed minimum-order fee? (assets/min-order-fee.js owns it; we only
 *  need to know not to count it.) */
function isMinOrderFeeLine(line) {
  return Boolean(prop(line, '_min_order_fee')) || String(line.sku || '').startsWith('MIN-ORDER-FEE-');
}

/** Cart unit count EXCLUDING every theme-managed line — what the cart-count badge should show.
 *  Must agree with snippets/cart-display-count.liquid and assets/min-order-fee.js, or whichever
 *  module dispatches last wins and the badge flickers between two numbers. */
function realCount(cart) {
  return cart.items.reduce((n, line) => (isDepositLine(line) || isMinOrderFeeLine(line) ? n : n + line.quantity), 0);
}

/** deposit line key -> its part's line key, so the deposit row can be nested under its part. */
let depositPartKey = new Map();
function buildDepositMap(cart) {
  const partKeyByProduct = new Map();
  for (const line of cart.items) {
    if (isDepositLine(line)) continue;
    const pid = String(prop(line, P_PART_PRODUCT) || line.product_id);
    if (!partKeyByProduct.has(pid)) partKeyByProduct.set(pid, line.key);
  }
  const m = new Map();
  for (const line of cart.items) {
    if (!isDepositLine(line)) continue;
    const partKey = partKeyByProduct.get(String(prop(line, P_DEPOSIT_FOR) || ''));
    if (partKey) m.set(line.key, partKey);
  }
  depositPartKey = m;
}

/**
 * The deposits the cart SHOULD have: one per distinct core part present.
 * @param {{items: any[]}} cart
 * @returns {Map<string, string>} productId -> deposit variant id
 */
function desiredDeposits(cart) {
  const desired = new Map();
  for (const line of cart.items) {
    if (isDepositLine(line)) continue;
    const variantId = prop(line, P_PART_VARIANT);
    if (!variantId) continue;
    const productId = String(prop(line, P_PART_PRODUCT) || line.product_id);
    desired.set(productId, String(variantId));
  }
  return desired;
}

/**
 * Keep exactly one correct deposit line per core part. Sequential, idempotent, fail-open.
 */
async function reconcile() {
  if (running) {
    rerunRequested = true;
    return;
  }
  running = true;
  try {
    const cart = await getCart();
    if (!cart || !Array.isArray(cart.items)) return;

    const desired = desiredDeposits(cart);

    const deposits = cart.items.filter(isDepositLine).map((l) => ({
      key: l.key,
      variantId: String(l.variant_id),
      quantity: l.quantity,
      forId: String(prop(l, P_DEPOSIT_FOR) || ''),
    }));

    // Decide which existing deposit lines to keep vs. remove. We only manage lines WE tagged
    // (`_core_for` present); an untagged CORE-DEP line is left alone (could be human-added).
    const keep = new Map(); // productId -> deposit kept
    const remove = [];
    for (const dep of deposits) {
      if (!dep.forId) continue;
      const wantVariant = desired.get(dep.forId);
      if (!wantVariant || wantVariant !== dep.variantId || keep.has(dep.forId)) {
        remove.push(dep); // part gone, wrong amount, or duplicate
      } else {
        keep.set(dep.forId, dep);
      }
    }

    let changed = false;

    // Re-read the cart right before any change.js so we never target a line that vanished since
    // the initial read (cleared in another tab, or a concurrent module touched the cart). Issuing
    // change.js for an absent line returns a harmless 400 but logs a red console line, so we skip
    // it. Only re-read when there's actually something to remove or re-quantify.
    const needsChange = remove.length > 0 || [...keep.values()].some((d) => d.quantity !== 1);
    let liveKeys = null;
    if (needsChange) {
      const beforeChange = await getCart();
      if (beforeChange && Array.isArray(beforeChange.items)) liveKeys = new Set(beforeChange.items.map((l) => l.key));
    }

    // 1. Remove stale/duplicate/mismatched deposits.
    for (const dep of remove) {
      if (liveKeys && !liveKeys.has(dep.key)) continue; // already gone — don't issue a doomed change.js
      if (await mutateOnce('/cart/change.js', { id: dep.key, quantity: 0 })) changed = true;
    }
    // 2. Force any kept deposit back to qty 1 (deposits are one-time).
    for (const [, dep] of keep) {
      if (dep.quantity !== 1) {
        if (liveKeys && !liveKeys.has(dep.key)) continue; // gone — skip
        if (await mutateOnce('/cart/change.js', { id: dep.key, quantity: 1 })) changed = true;
      }
    }
    // 3. Add a deposit for any core part that doesn't have one yet.
    for (const [productId, variantId] of desired) {
      if (keep.has(productId)) continue;
      const ok = await mutateOnce('/cart/add.js', {
        id: Number(variantId),
        quantity: 1,
        properties: { [P_DEPOSIT]: 'true', [P_DEPOSIT_FOR]: productId },
      });
      if (ok) {
        changed = true;
      } else {
        // Fail loud (but never block). A Shopify Flow backstop can flag orders whose core part
        // has the metafield but no matching deposit line. See build plan, "Coverage gap".
        console.warn(`[core-deposit] could not add deposit variant ${variantId} for product ${productId}`);
      }
    }

    const finalCart = changed ? await getCart() : cart;
    if (finalCart) {
      buildDepositMap(finalCart);
      deferDecorate();
      if (changed) {
        // Ask the theme's cart UI to re-render. source:SOURCE so our own listener ignores it.
        // itemCount excludes deposits so the cart-count badge shows real parts only.
        document.dispatchEvent(new CartUpdateEvent(finalCart, SOURCE, { source: SOURCE, itemCount: realCount(finalCart) }));
      }
    }
  } catch (_e) {
    /* fail open — a deposit hiccup must never block the cart */
  } finally {
    running = false;
    if (rerunRequested) {
      rerunRequested = false;
      reconcile();
    }
  }
}

const requestReconcile = debounce(reconcile, 150);

/** Cart events that should trigger a reconcile — but never our own re-render dispatch. */
function onCartEvent(event) {
  if (event && event.detail && event.detail.data && event.detail.data.source === SOURCE) return;
  requestReconcile();
  deferDecorate();
}

/* ── Cosmetic decoration: relabel deposit rows + lock their controls. Correctness is owned by
   reconcile, not by this — so decoration is purely presentational and fully fail-safe. ── */

let decoratePending = false;
function deferDecorate() {
  if (decoratePending) return;
  decoratePending = true;
  requestAnimationFrame(() => {
    decoratePending = false;
    decorateDepositRows();
  });
}

/**
 * Relabel any cart row that is a deposit line and lock it (CSS keys off [data-core-deposit]).
 * Idempotent — safe to call repeatedly and after every Horizon morph.
 * @param {ParentNode} [root]
 */
function decorateDepositRows(root = document) {
  let rows;
  try {
    rows = root.querySelectorAll('.cart-items__table-row');
  } catch (_e) {
    return;
  }
  for (const row of rows) {
    const price = row.querySelector('product-price[data-sku]');
    const sku = price && price.dataset.sku ? price.dataset.sku : '';
    if (!sku.startsWith(DEPOSIT_SKU_PREFIX)) continue;

    // Nest the deposit row directly beneath the part it belongs to (best-effort; if it can't be
    // matched it stays in place — still styled as a subordinate, locked line either way).
    const depKey = row.getAttribute('data-key');
    const partKey = depKey ? depositPartKey.get(depKey) : null;
    if (partKey && row.parentNode) {
      const sel = `.cart-items__table-row[data-key="${window.CSS && CSS.escape ? CSS.escape(partKey) : partKey}"]`;
      const partRow = row.parentNode.querySelector(sel);
      if (partRow && partRow.nextElementSibling !== row) partRow.insertAdjacentElement('afterend', row);
    }

    if (row.hasAttribute('data-core-deposit')) continue; // labels/styling applied once
    row.setAttribute('data-core-deposit', '');

    const title = row.querySelector('.cart-items__title');
    if (title) title.textContent = 'Core Deposit (refundable)';

    // The variant option line just repeats the dollar amount the price column already shows.
    const variants = row.querySelector('.cart-items__variants');
    if (variants) variants.style.display = 'none';

    const details = row.querySelector('.cart-items__details');
    if (details && !details.querySelector('.cart-items__core-note')) {
      const note = document.createElement('span');
      note.className = 'cart-items__core-note';
      note.textContent = 'Refunded when your old core is returned.';
      details.appendChild(note);
    }
  }
}

let observer = null;
function observeCart() {
  if (observer) return;
  // Re-apply decoration after Horizon morphs the cart section (the fleet.js self-heal pattern).
  observer = new MutationObserver(() => deferDecorate());
  const attach = () => {
    for (const host of document.querySelectorAll('.cart-items__wrapper, cart-items-component')) {
      observer.observe(host, { childList: true, subtree: true });
    }
  };
  attach();
  // Horizon fires a NON-bubbling 'dialog:open' on the dialog element (assets/dialog.js) — only a
  // capture-phase document listener sees it. ('dialog:opened' is never dispatched by anything.)
  document.addEventListener('dialog:open', attach, true);
}

function start() {
  document.addEventListener(ThemeEvents.cartUpdate, onCartEvent); // add-to-cart, qty change, remove
  document.addEventListener('cart:refresh', onCartEvent); // cart morph refresh
  document.addEventListener(
    'dialog:open',
    (e) => {
      const t = e.target;
      const looksLikeCart =
        t && t.matches && (t.matches('cart-drawer-component, cart-drawer, dialog[id*="cart"]') ||
          (t.querySelector && t.querySelector('cart-drawer, .cart-items__table-row')));
      if (looksLikeCart) {
        requestReconcile();
        deferDecorate();
      }
    },
    true // non-bubbling event — see observeCart()
  );

  observeCart();

  // Safety-net pass once the page is idle: heals carts created before this shipped (or via a
  // channel that bypasses the form) and decorates what's on screen. Only hit the network when a
  // cart is actually rendered here (cart page or the preloaded drawer with items) — an empty cart
  // has no rows, so a normal page load with no cart costs zero requests.
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
  idle(() => {
    if (document.querySelector('.cart-items__table-row')) reconcile();
    decorateDepositRows();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
